/* Quiz player (02 §2.3.1, §3.3.3).
 *
 * The load-bearing rule, and the whole reason this mechanic exists:
 * CORRECTNESS IS NEVER VISIBLE BEFORE CONFIDENCE IS SUBMITTED. Confidence is
 * captured first, then the answer is graded and revealed together. Any leak —
 * an early tick, a disabled wrong option, a colour change — would destroy the
 * calibration signal the product is built around (ADR-12).
 *
 * Items and options are shuffled per attempt. Exact-answer and code items keep
 * their internal order, because there is nothing meaningful to shuffle.
 *
 * Local grading is provisional: the server re-grades everything at sync and its
 * value wins (A14). We show the local score immediately so an offline attempt
 * still gives feedback.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { PASS_SCORE } from '@ascent/shared';
import type { UnitDto } from '../api';
import { Markdown } from '../markdown';
import { CodeRunner, type ExerciseSpec } from '../exercise/code-runner';
import { pythonRunnable } from '../exercise/pack';

type Confidence = 0.5 | 0.7 | 0.9;

const CONFIDENCE_BUTTONS: { value: Confidence; label: string; sub: string }[] = [
  { value: 0.9, label: 'Sure', sub: '90%' },
  { value: 0.7, label: 'Likely', sub: '70%' },
  { value: 0.5, label: 'Guess', sub: '50%' },
];

interface QuizItem {
  id: string;
  kind: string;
  stem_md: string;
  explanation_md: string;
  options?: string[];
  correct_index?: number;
  answer_mode?: string;
  accepted_answers?: string[];
  code_md?: string;
  /** code_python */
  starter_code?: string;
  checks_py?: string;
  /** code_sql */
  seed_sql?: string;
  expected_rows?: unknown[][];
  order_sensitive?: boolean;
}

const isCodeItem = (kind: string): boolean => kind === 'code_python' || kind === 'code_sql';

/** The runnable exercise behind a code item, in the runner's own shape. */
function exerciseOf(item: QuizItem): ExerciseSpec | null {
  if (item.kind === 'code_python') {
    return { type: 'python', starter_code: item.starter_code ?? '', checks_py: item.checks_py ?? '' };
  }
  if (item.kind === 'code_sql') {
    return {
      type: 'sql',
      seed_sql: item.seed_sql ?? '',
      expected_rows: item.expected_rows ?? [],
      order_sensitive: item.order_sensitive === true,
    };
  }
  return null;
}

interface Presented {
  item: QuizItem;
  /** Shuffled option order, as displayed; maps display index -> original index. */
  optionOrder: number[];
}

export interface QuizAnswer {
  item_id: string;
  answer: unknown;
  /** Null only on a skipped item — a skip claims no confidence (02 §6.10). */
  confidence: Confidence | null;
  correct: boolean;
  skipped?: boolean;
}

/** Fisher–Yates with a per-attempt seed so an in-progress attempt is stable. */
function shuffle<T>(items: T[], seed: number): T[] {
  const out = [...items];
  let s = seed || 1;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const j = s % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

function gradeLocally(item: QuizItem, answer: unknown): boolean {
  const a = (answer ?? {}) as Record<string, unknown>;
  if (item.kind === 'mcq' || (item.kind === 'code_reading' && item.options)) {
    return a['selected_index'] === item.correct_index;
  }
  if (item.kind === 'code_reading') {
    const text = String(a['text'] ?? '').trim().toLowerCase();
    return (item.accepted_answers ?? []).some((x) => x.trim().toLowerCase() === text);
  }
  return a['client_passed'] === true;
}

export function QuizScreen({
  unit, onSubmit, onCancel, onOpenSettings,
}: {
  unit: UnitDto;
  onSubmit: (answers: QuizAnswer[], startedAt: string) => Promise<void>;
  onCancel: () => void;
  onOpenSettings?: () => void;
}) {
  const [seed] = useState(() => Math.floor(Math.random() * 2 ** 31));
  const [startedAt] = useState(() => new Date().toISOString());
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [confidence, setConfidence] = useState<Confidence | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  /** null until the code item has been Run at least once. */
  const [codePassed, setCodePassed] = useState<boolean | null>(null);
  /** null while unknown; false = offline with no pack, so python cannot run. */
  const [pythonOk, setPythonOk] = useState<boolean | null>(null);
  const [startAcknowledged, setStartAcknowledged] = useState(false);

  const rawItems = (unit as unknown as { items?: QuizItem[] }).items ?? [];

  useEffect(() => { void pythonRunnable().then(setPythonOk); }, []);

  const presented: Presented[] = useMemo(
    () =>
      shuffle(rawItems, seed).map((item, i) => ({
        item,
        optionOrder: item.options
          ? shuffle(item.options.map((_, k) => k), seed + i + 1)
          : [],
      })),
    [rawItems, seed],
  );

  const current = presented[index];
  const done = index >= presented.length;

  const buildAnswer = useCallback((p: Presented): unknown => {
    if (p.item.options) {
      // Map the DISPLAY index back to the original option index.
      return { selected_index: selected === null ? -1 : p.optionOrder[selected] };
    }
    if (p.item.kind === 'code_reading') return { text };
    /* Code items are client-attested: the checks ran on this device and the
     * server records the verdict, never the source (ADR-02).
     *
     * `code: ''` is deliberate. 05 §3.5 makes `code` a required field while
     * 02 §2.8 forbids learner source from leaving the device; CR-002 is Pending,
     * so this is its documented fallback — satisfy the wire shape, send nothing
     * of the learner's. Do not "fix" this by sending the real source. */
    return { client_passed: codePassed === true, code: '' };
  }, [selected, text, codePassed]);

  const codeItem = current ? isCodeItem(current.item.kind) : false;
  const answerReady = codeItem
    ? codePassed !== null
    : current?.item.options
      ? selected !== null
      : text.trim().length > 0;

  const submitItem = useCallback(() => {
    if (!current || confidence === null || !answerReady) return;
    const answer = buildAnswer(current);
    const correct = gradeLocally(current.item, answer);
    setAnswers((prev) => [...prev, { item_id: current.item.id, answer, confidence, correct }]);
    setRevealed(true);
  }, [current, confidence, answerReady, buildAnswer]);

  /** A python item that cannot run offline: scored incorrect, no confidence. */
  const skipItem = useCallback(() => {
    if (!current) return;
    setAnswers((prev) => [
      ...prev,
      { item_id: current.item.id, answer: null, confidence: null, correct: false, skipped: true },
    ]);
    setRevealed(true);
  }, [current]);

  const next = useCallback(() => {
    setIndex((i) => i + 1);
    setSelected(null);
    setText('');
    setConfidence(null);
    setRevealed(false);
    setCodePassed(null);
  }, []);

  const finish = useCallback(async () => {
    setSubmitting(true);
    try {
      await onSubmit(answers, startedAt);
    } finally {
      setSubmitting(false);
    }
  }, [answers, startedAt, onSubmit]);

  // ---- Completion recap -------------------------------------------------
  if (done) {
    const correct = answers.filter((a) => a.correct).length;
    // A skipped item stays in the DENOMINATOR — it was on the quiz and it was
    // not answered (11 §4.4). It is only excluded from calibration.
    const score = answers.length ? correct / answers.length : 0;
    const passed = score >= PASS_SCORE;
    const scored = answers.filter((a) => !a.skipped && a.confidence !== null);
    const claimed = scored.length
      ? scored.reduce((s, a) => s + (a.confidence ?? 0), 0) / scored.length
      : 0;
    const skippedCount = answers.filter((a) => a.skipped).length;

    return (
      <main className="screen">
        <p className="eyebrow">{passed ? 'Passed' : 'Not yet'}</p>
        <h1>{Math.round(score * 100)}%</h1>
        <p>
          {correct} of {answers.length} correct. Pass bar is {Math.round(PASS_SCORE * 100)}%.
          Retakes are always allowed, with no cooldown.
        </p>
        {skippedCount > 0 ? (
          <p className="hint">
            {skippedCount} {skippedCount === 1 ? 'item' : 'items'} could not run without the
            offline pack and {skippedCount === 1 ? 'was' : 'were'} scored unanswered. {skippedCount === 1 ? 'It is' : 'They are'} left
            out of your calibration — you never claimed a confidence for {skippedCount === 1 ? 'it' : 'them'}.
          </p>
        ) : null}

        {/* The calibration mini-readout — claimed vs actual, side by side. */}
        <div className="card">
          <div className="card__title"><span>Calibration</span></div>
          <p style={{ margin: 0 }}>
            You claimed <strong>{Math.round(claimed * 100)}%</strong> average confidence
            and scored <strong>{Math.round(score * 100)}%</strong>.
            {claimed - score > 0.15
              ? ' That gap is the pattern this app is built to catch.'
              : claimed - score < -0.15
                ? ' You were harder on yourself than the evidence warranted.'
                : ' Well calibrated.'}
          </p>
        </div>

        <div className="card">
          <div className="card__title"><span>Per item</span></div>
          <ul className="recap">
            {answers.map((a, i) => (
              <li key={a.item_id} className={a.correct ? 'recap--ok' : 'recap--no'}>
                <span className="recap__n">{i + 1}</span>
                <span className="recap__mark">
                  {a.skipped ? 'unanswered' : a.correct ? 'correct' : 'wrong'}
                </span>
                <span className="recap__conf">
                  {a.skipped
                    ? 'no pack'
                    : CONFIDENCE_BUTTONS.find((c) => c.value === a.confidence)?.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <button type="button" onClick={() => void finish()} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save and finish'}
        </button>
        <p className="hint">
          The server re-grades every item and its score is the one that counts.
        </p>
      </main>
    );
  }

  if (!current) {
    return (
      <main className="screen">
        <h1>This quiz has no items</h1>
        <button type="button" onClick={onCancel}>Back</button>
      </main>
    );
  }

  /* ---- Pre-start warning (02 §6.10) ---------------------------------------
   * If the attempt contains a `python` item this device cannot run, say so
   * BEFORE the first item rather than ambushing the learner three items in with
   * an unanswerable question that costs them the pass. */
  const blockedPythonItems = pythonOk === false
    ? presented.filter((p) => p.item.kind === 'code_python').length
    : 0;

  if (blockedPythonItems > 0 && !startAcknowledged && index === 0 && answers.length === 0) {
    return (
      <main className="screen">
        <button type="button" className="back" onClick={onCancel}>← Leave quiz</button>
        <p className="eyebrow">{unit.title}</p>
        <h1>{blockedPythonItems} {blockedPythonItems === 1 ? 'item needs' : 'items need'} the offline pack</h1>
        <p>
          You are offline and the Python runtime is not on this device. Starting
          now scores {blockedPythonItems === 1 ? 'that item' : 'those items'} unanswered —
          it still counts in the denominator, so the pass bar gets harder.
        </p>
        <p className="hint">
          {onOpenSettings
            ? 'Install the pack in Settings (Wi-Fi recommended), or start anyway — retakes are always allowed.'
            : 'Install the pack from Settings once you are online, or start anyway — retakes are always allowed.'}
        </p>
        <div className="grades" style={{ marginTop: '1.25rem' }}>
          {onOpenSettings ? (
            <button type="button" className="grade grade--good" onClick={onOpenSettings}>
              Open Settings
            </button>
          ) : null}
          <button type="button" className="grade grade--muted" onClick={() => setStartAcknowledged(true)}>
            Start anyway
          </button>
        </div>
      </main>
    );
  }

  const item = current.item;
  const exercise = exerciseOf(item);
  const itemBlocked = item.kind === 'code_python' && pythonOk === false;

  return (
    <main className="screen">
      {/* A way out. 02 §3.3.3 allows abandoning an attempt, and a Quiz opened by
       * mistake from the Atlas must not be a one-way door. Answers live only in
       * this component until Save and finish, so leaving discards them — the
       * confirm step says that instead of quietly dropping four answers on a
       * stray tap. No native confirm(): it blanks PWAs on iOS. */}
      <button
        type="button"
        className="back"
        onClick={() => (answers.length === 0 ? onCancel() : setConfirmLeave(true))}
      >
        ← Leave quiz
      </button>

      {confirmLeave ? (
        <div className="card">
          <div className="card__title"><span>Leave this attempt?</span></div>
          <p style={{ margin: '0 0 0.875rem' }}>
            {answers.length === 1
              ? 'The one answer so far is discarded'
              : `The ${answers.length} answers so far are discarded`}
            {' '}— nothing reaches the server until you finish the quiz. Retakes are
            always allowed, so nothing is lost but the time.
          </p>
          <div className="grades">
            <button type="button" className="grade grade--critical" onClick={onCancel}>Leave</button>
            <button type="button" className="grade grade--muted" onClick={() => setConfirmLeave(false)}>
              Stay
            </button>
          </div>
        </div>
      ) : null}

      <p className="eyebrow">
        {unit.title} · item {index + 1} of {presented.length}
      </p>

      <div className="progressdots" aria-hidden="true">
        {presented.map((_, i) => (
          <span key={i} className={`dot ${i < index ? 'dot--done' : i === index ? 'dot--now' : ''}`} />
        ))}
      </div>

      <div className="card">
        <Markdown source={item.stem_md} />
        {item.code_md ? <Markdown source={item.code_md} /> : null}
      </div>

      {/* Options. While un-revealed, NOTHING here encodes correctness. */}
      {exercise ? null : item.options ? (
        <div className="options">
          {current.optionOrder.map((originalIndex, displayIndex) => {
            const isChosen = selected === displayIndex;
            const isCorrect = revealed && originalIndex === item.correct_index;
            const isWrongChoice = revealed && isChosen && originalIndex !== item.correct_index;
            return (
              <button
                key={originalIndex}
                type="button"
                className={`option ${isChosen ? 'option--chosen' : ''} ${
                  isCorrect ? 'option--correct' : ''} ${isWrongChoice ? 'option--wrong' : ''}`}
                disabled={revealed}
                onClick={() => setSelected(displayIndex)}
              >
                <Markdown source={item.options![originalIndex]!} />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <label htmlFor="exact">Your answer</label>
          <input
            id="exact" type="text" value={text} disabled={revealed}
            autoCapitalize="off" autoCorrect="off" spellCheck={false}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      )}

      {/* Confidence — required BEFORE anything is revealed, and for a code item
       * before Run: the checks are feedback, and feedback ahead of the claim
       * would let the learner calibrate against the answer, not their belief. */}
      {!revealed ? (
        <div className="card">
          <div className="card__title"><span>How sure are you?</span></div>
          <div className="confidence">
            {CONFIDENCE_BUTTONS.map((c) => (
              <button
                key={c.value}
                type="button"
                className={`conf ${confidence === c.value ? 'conf--on' : ''}`}
                onClick={() => setConfidence(c.value)}
              >
                <span className="conf__label">{c.label}</span>
                <span className="conf__sub">{c.sub}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card__title">
            <span>
              {answers[answers.length - 1]?.skipped
                ? 'Unanswered'
                : answers[answers.length - 1]?.correct ? 'Correct' : 'Not quite'}
            </span>
            <span className="card__meta">
              {answers[answers.length - 1]?.skipped
                ? 'no confidence claimed'
                : `you said ${CONFIDENCE_BUTTONS.find((c) => c.value === answers[answers.length - 1]?.confidence)?.label ?? ''}`}
            </span>
          </div>
          <Markdown source={item.explanation_md} />
        </div>
      )}

      {/* The runner sits BELOW the confidence card on purpose — the order on
       * screen is the order of the mechanic. */}
      {exercise ? (
        itemBlocked ? (
          <div className="card card--blocked">
            <div className="card__title"><span>This item needs the offline pack</span></div>
            <p style={{ margin: '0 0 0.875rem' }}>
              Offline, with no Python runtime on this device. Marking it unanswered
              is the honest option: it scores incorrect, and it stays out of your
              calibration because you never claimed a confidence for it.
            </p>
            {!revealed ? (
              <button type="button" onClick={skipItem}>Mark unanswered and continue</button>
            ) : null}
          </div>
        ) : (
          <CodeRunner
            key={item.id}
            exercise={exercise}
            runDisabled={confidence === null || revealed}
            runDisabledHint={revealed ? undefined : 'Set your confidence first — then Run.'}
            /* A completed run is an answer, whatever it produced. Only `ok`
             * used to count, so a syntax error or a timeout left the item with
             * no answer and `Submit item` disabled forever — no way forward and
             * no way to skip. A failed run is an honest wrong answer. */
            onOutcome={(o) => {
              if (o.status === 'ok') setCodePassed(o.passed);
              else if (o.status === 'error' || o.status === 'timeout') setCodePassed(false);
            }}
            onOpenSettings={onOpenSettings}
          />
        )
      ) : null}

      {revealed ? (
        <button type="button" onClick={next}>
          {index + 1 === presented.length ? 'See results' : 'Next item'}
        </button>
      ) : (
        <button type="button" onClick={submitItem} disabled={!answerReady || confidence === null}>
          Submit item
        </button>
      )}

      {!revealed ? (
        <p className="hint">
          {/* A code item's order is confidence → Run → Submit, so the generic
            * "choose an answer" line described the wrong mechanic entirely. */}
          {confidence === null
            ? codeItem
              ? 'Rate your confidence first — that unlocks Run.'
              : 'Choose an answer, then rate your confidence.'
            : !answerReady
              ? codeItem
                ? 'Run your query to answer this item. Running again costs nothing.'
                : 'Choose an answer to submit.'
              : 'Correctness is revealed after you submit.'}
        </p>
      ) : null}
    </main>
  );
}
